
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  DROPDOWN_AREA_PROMPT,
  LOAD_IMAGE_ERROR,
  MAX_AVATAR_WIDTH_AND_HEIGHT,
  MAX_UPLOADED_IMAGE_HEIGHT,
  MAX_UPLOADED_IMAGE_WIDTH,
  NOT_SUPPORTED_IMAGE_EXTENSION,
  UPLOAD_AVATAR_TIMEOUT,
  UPLOAD_IMAGE_ERROR,
} from '../../utils/constants';
import { isSupportedImageExtension } from '../../utils/functions';
import { ImageWithDimensions, UploadImageType } from '@/utils/models';
import { WhiteboardPenColors } from '@/utils/whiteboard';

@Component({})
export default class ImageUploader extends Vue {
  @Prop() private uploadImageType!: UploadImageType;
  @Prop() private hidePopup!: () => void;
  @Prop() private showLoader!: () => void;
  @Prop() private dropAreaHeight!: string;
  @Prop() private isErrorOnUpload?: boolean;
  @Prop() private noResize!: boolean;

  @Emit() private hideLoader(checkIfError: boolean): void {}

  @Watch('isErrorOnUpload')
  private onIsErrorOnUpload(isError: boolean) {
    if (isError) {
      this.onError();
    } else {
      this.setDropdownAreaPromptToDefault();
    }
  }

  dropdownAreaPrompt = DROPDOWN_AREA_PROMPT;

  private onError(): void {
    this.dropdownAreaPrompt = UPLOAD_IMAGE_ERROR;
    (this.$refs.dropdownAreaRef as HTMLDivElement).style.color = WhiteboardPenColors.red;
  }

  private onReadFileError(): void {
    this.dropdownAreaPrompt = LOAD_IMAGE_ERROR;
    (this.$refs.dropdownAreaRef as HTMLDivElement).style.color = WhiteboardPenColors.red;
  }

  private openFileExplorer(): void {
    (this.$refs.fileInputRef as HTMLInputElement).click();
  }

  private onFileDropped(e: DragEvent): void {
    if (e.dataTransfer && e.dataTransfer.files) {
      this.onUpload(e.dataTransfer.files[0]);
    }
  }

  private resetInputState(): void {
    (this.$refs.fileInputRef as HTMLInputElement).value = '';
  }

  private onUploadByBrowsing(e: any): void {
    if (e.target.files && e.target.files[0]) {
      this.onUpload(e.target.files[0]);
    }
  }

  private onUpload(file: any): void {
    this.showLoadingPopup();

    if (this.uploadImageType === 'upload image') {
      this.processFileUploadForImage(file);
    } else if (this.uploadImageType === 'upload avatar') {
      this.processFileUploadForAvatar(file);
    }
  }

  private showLoadingPopup(): void {
    if (this.showLoader) {
      this.showLoader();
      setTimeout(() => this.hideLoader(true), UPLOAD_AVATAR_TIMEOUT);
    }
  }

  private processFileUploadForAvatar(file: any): void {
    if (!isSupportedImageExtension(file.type)) {
      this.onNotSupportedFileExtension(file.name);
    } else {
      this.setResizeBase64WithDimensions([file] as unknown as FileList, true);
      this.setDropdownAreaPromptToDefault();
    }
    this.hideWhiteBorder();
  }

  private processFileUploadForImage(file: any): void {
    if (!isSupportedImageExtension(file.type)) {
      this.onNotSupportedFileExtension(file.name);
    } else {
      this.setResizeBase64WithDimensions([file] as unknown as FileList, false);
      this.setDropdownAreaPromptToDefault();
      if (this.hidePopup) {
        this.hidePopup();
      }
    }
    this.hideWhiteBorder();
  }

  private onNotSupportedFileExtension(fileName: string): void {
    const notSupportedExtensionMessage = NOT_SUPPORTED_IMAGE_EXTENSION(fileName);
    this.dropdownAreaPrompt = notSupportedExtensionMessage;
    (this.$refs.dropdownAreaRef as HTMLDivElement).style.color = WhiteboardPenColors.red;
  }

  private setDropdownAreaPromptToDefault(): void {
    this.dropdownAreaPrompt = DROPDOWN_AREA_PROMPT;
    (this.$refs.dropdownAreaRef as HTMLDivElement).style.color = '#444444';
  }

  private onFileDraggedOver(): void {
    if (!(this.$refs.dropdownAreaRef as HTMLDivElement).classList.contains('image-uploader__dropdown-area--focused')) {
      (this.$refs.dropdownAreaRef as HTMLDivElement).classList.remove('image-uploader__dropdown-area--not-focused');
      (this.$refs.dropdownAreaRef as HTMLDivElement).classList.add('image-uploader__dropdown-area--focused');
    }
  }

  private onFileDraggedLeft(): void {
    this.hideWhiteBorder();
  }

  private hideWhiteBorder(): void {
    (this.$refs.dropdownAreaRef as HTMLDivElement).classList.add('image-uploader__dropdown-area--not-focused');
    (this.$refs.dropdownAreaRef as HTMLDivElement).classList.remove('image-uploader__dropdown-area--focused');
  }

  private resizeBase64String(
    base64: string,
    newWidth: number,
    newHeight: number,
  ): Promise<{ base64: string; width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const img = document.createElement('img');
      img.src = base64;
      img.onload = () => {
        if (context) {
          let percentageOfOriginalSize: number;
          if (img.width <= newWidth && img.height <= newHeight) {
            percentageOfOriginalSize = 1;
          } else {
            const widthPercentage = newWidth / img.width;
            const heightPercentage = newHeight / img.height;
            if (widthPercentage > heightPercentage) {
              percentageOfOriginalSize = heightPercentage;
            } else {
              percentageOfOriginalSize = widthPercentage;
            }
          }

          if (percentageOfOriginalSize !== 1) {
            const width = img.width * percentageOfOriginalSize;
            const height = img.height * percentageOfOriginalSize;

            canvas.width = width;
            canvas.height = height;

            context.scale(percentageOfOriginalSize, percentageOfOriginalSize);
            context.drawImage(img, 0, 0);
            resolve({ base64: canvas.toDataURL(), width: width, height: height });
          } else {
            resolve({ base64, width: img.width, height: img.height });
          }
        }
      };
    });
  }

  private setResizeBase64WithDimensions(fileList: FileList, isAvatar: boolean): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    if (fileList && fileList[0]) {
      const fr = new FileReader();
      let base64String;
      fr.onloadend = function () {
        base64String = fr.result;
        if (isAvatar) {
          that
            .resizeBase64String(base64String as string, MAX_AVATAR_WIDTH_AND_HEIGHT, MAX_AVATAR_WIDTH_AND_HEIGHT)
            .then((result: ImageWithDimensions) => {
              // that.$store.commit('SetAvatarBase64', result.base64);
              that.applyImage(result.base64);
              that.hideLoader(false);
            })
            .catch((e: any) => {
              that.onReadFileError();
              that.hideLoader(false);
            });
        } else {
          const maxWidth = that.noResize ? Number.MAX_VALUE : MAX_UPLOADED_IMAGE_WIDTH;
          const maxHeight = that.noResize ? Number.MAX_VALUE : MAX_UPLOADED_IMAGE_HEIGHT;
          that
            .resizeBase64String(base64String as string, maxWidth, maxHeight)
            .then((result: ImageWithDimensions) => {
              // that.$store.commit("SetUploadedImageBase64", result);
              that.applyImage(result);
              that.hideLoader(false);
            })
            .catch((e: any) => {
              that.onReadFileError();
              that.hideLoader(false);
            });
        }
      };
      fr.readAsDataURL(fileList[0]);
    }
  }
  @Emit()
  applyImage(imageUrl: string | ImageWithDimensions): string | ImageWithDimensions {
    return imageUrl;
  }
}
